import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  getGetBrokersMembersMemberIdIchraDataQueryKey,
  usePutBrokersMemberIchraConfigOptedIn,
  usePutBrokersMemberIchraConfigQmeTreatment,
} from 'utils/web_api'
import type {
  MemberICHRAConfig,
  PutBrokersMemberIchraConfigOptedInValue,
  PutBrokersMemberIchraConfigQmeTreatmentValue,
} from 'utils/web_api.schemas'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select'

function getOptedInFormValue (optedIn: boolean | null | undefined): string {
  switch (optedIn) {
    case true:
      return 'true'
    case false:
      return 'false'
    default:
      return 'null'
  }
}

const OPTED_IN_VALUES = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
  { value: 'null', label: 'No selection' },
]

const QME_TREATMENT_VALUES = [
  { value: 'disabled', label: 'Disabled' },
  { value: 'all_allowed', label: 'All Allowed' },
  { value: 'copay_only', label: 'Copay Only' },
]

interface MemberIchraConfigProps {
  member_ichra_config: MemberICHRAConfig
}

const MemberIchraConfig = ({
  member_ichra_config: memberIchraConfig,
}: MemberIchraConfigProps) => {
  const queryClient = useQueryClient()

  const optedInMutation = usePutBrokersMemberIchraConfigOptedIn({
    mutation: {
      onSuccess: (memberIchraConfig) => {
        queryClient.invalidateQueries({
          queryKey: getGetBrokersMembersMemberIdIchraDataQueryKey(
            memberIchraConfig.member_id
          ),
        })
        toast.success('Member ICHRA Config opted_in updated', {
          position: 'top-center',
        })
      },
      onError: () => {
        toast.error('Failed to update Member ICHRA Config', {
          position: 'top-center',
        })
      },
    },
  })

  const qmeTreatmentMutation = usePutBrokersMemberIchraConfigQmeTreatment({
    mutation: {
      onSuccess: (memberIchraConfig) => {
        queryClient.invalidateQueries({
          queryKey: getGetBrokersMembersMemberIdIchraDataQueryKey(
            memberIchraConfig.member_id
          ),
        })
        toast.success('Member ICHRA Config qme_treatment updated', {
          position: 'top-center',
        })
      },
      onError: () => {
        toast.error('Failed to update Member ICHRA Config', {
          position: 'top-center',
        })
      },
    },
  })

  const {
    plan_year: planYear,
    qme_treatment: qmeTreatment,
    opted_in: optedIn,
  } = memberIchraConfig

  const handleChange = (field: string, value: string) => {
    if (field === 'opted_in') {
      optedInMutation.mutate({
        data: {
          member_ichra_config_id: memberIchraConfig.id,
          value: value as PutBrokersMemberIchraConfigOptedInValue,
        },
      })
    } else if (field === 'qme_treatment') {
      qmeTreatmentMutation.mutate({
        data: {
          member_ichra_config_id: memberIchraConfig.id,
          value: value as PutBrokersMemberIchraConfigQmeTreatmentValue,
        },
      })
    }
  }

  return (
    <div className='flex flex-col'>
      <h2 className='h2 mb-2'>Member Ichra Config ({planYear})</h2>
      <div className='flex space-x-4'>
        <div>
          <label
            className='label-default font-medium text-ds-text-secondary mb-2'
            htmlFor='opted_in'
          >
            Opted In?
          </label>
          <Select
            onValueChange={(value) => handleChange('opted_in', value)}
            value={getOptedInFormValue(optedIn)}
            disabled={optedInMutation.isPending}
          >
            <SelectTrigger className='w-[180px]'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OPTED_IN_VALUES.map(({ value, label }, index) => (
                <SelectItem key={index} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label
            className='label-default font-medium text-ds-text-secondary mb-2'
            htmlFor='qme_treatment'
          >
            QME Treatment
          </label>
          <Select
            onValueChange={(value) => handleChange('qme_treatment', value)}
            value={qmeTreatment}
            disabled={qmeTreatmentMutation.isPending}
          >
            <SelectTrigger className='w-[180px]'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {QME_TREATMENT_VALUES.map(({ value, label }, index) => (
                <SelectItem key={index} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  )
}

export default MemberIchraConfig
